/**
 * alerts-log-page.js
 *
 * Displays a historical log of all alerts fired by the server.
 * Each entry shows: date, coin, strategy, direction, entry price,
 * indicator snapshot at entry, exit price (if set), and % gain/loss.
 *
 * Users can record an exit by clicking "Log Exit" on any open trade.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/loader';
import ErrorBanner from '../components/error-banner';
import { formatPrice, formatPercentChange, formatRSI, formatDate, formatTime } from '../utils/format-helpers';
import { fetchAlertsLog, updateAlertLogEntry, isServerApiAvailable } from '../services/server-api';
import { useAuth } from '../context/auth-context';
import { hasAccess } from '../config/tier-config';
import { ALL_STRATEGIES } from '../services/alert-settings-service';
import './alerts-log-page.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Strategies renamed after launch — historical log entries are frozen with
// whatever name was live when they fired, so old and new names must both
// resolve to the same tier requirement. Also covers the short-form labels
// alert-service.js actually writes to logs/Telegram, which differ from
// ALL_STRATEGIES' longer display labels ('RSI-MTF' vs 'RSI Multi-Timeframe',
// 'Triple Alignment' vs 'Triple Alignment (W+D+4H)', etc).
const STRATEGY_LABEL_ALIASES = {
  'Rubberband':       'rubberband',
  'TopDog':           'topdog',
  'RSI-MTF':          'rsi-mtf',
  'Triple Alignment': 'triple-alignment',
  'Double Alignment': 'double-alignment',
};

// The reverse of the aliases above, scoped to labels alert-service.js
// currently writes (as opposed to legacy-only aliases like 'Rubberband' and
// 'TopDog') — used to build filter chips that will actually match future
// log entries for strategies with zero alerts fired so far.
const CURRENT_LOG_LABEL = {
  'triple-alignment': 'Triple Alignment',
  'double-alignment': 'Double Alignment',
  'rsi-mtf':           'RSI-MTF',
};

function getStrategyTier(strategyLabel) {
  const match = ALL_STRATEGIES.find(s => s.label === strategyLabel);
  if (match) return match.tier;
  const aliasKey = STRATEGY_LABEL_ALIASES[strategyLabel];
  if (aliasKey) return ALL_STRATEGIES.find(s => s.key === aliasKey)?.tier;
  return null; // unmapped strategy — fail open, don't hide it
}

function IndicatorPill({ label, value, color }) {
  return (
    <span className={`ind-pill ${color || ''}`}>
      {label}: {value ?? 'N/A'}
    </span>
  );
}

function StrategyBadge({ strategy }) {
  const map = {
    'Rubberband':       'badge-rb',
    'RBPullback':       'badge-rb',
    'Triple Alignment': 'badge-triple',
    'RSI-MTF':          'badge-mtf',
  };
  return <span className={`strategy-badge ${map[strategy] || ''}`}>{strategy}</span>;
}

// ─── Exit price modal ─────────────────────────────────────────────────────────

function ExitModal({ entry, onConfirm, onClose }) {
  const [exitPrice, setExitPrice] = useState('');
  const [error, setError]         = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    const val = parseFloat(exitPrice);
    if (!exitPrice || isNaN(val) || val <= 0) {
      setError('Please enter a valid price greater than 0.');
      return;
    }
    onConfirm(entry.id, val);
  }

  const pct = exitPrice && entry.entryPrice
    ? (((parseFloat(exitPrice) - entry.entryPrice) / entry.entryPrice) * 100 *
        (entry.direction === 'short' ? -1 : 1)).toFixed(2)
    : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Log Exit — {entry.symbol}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="modal-info">
            Entry price: <strong>{formatPrice(entry.entryPrice)}</strong>
            &nbsp;·&nbsp; Strategy: <strong>{entry.strategy}</strong>
            &nbsp;·&nbsp; Direction: <strong className={entry.direction}>{entry.direction?.toUpperCase()}</strong>
          </div>
          <form onSubmit={handleSubmit}>
            <label className="modal-label">Exit price (USD)</label>
            <input
              className="modal-input"
              type="number"
              step="any"
              placeholder="e.g. 65000"
              value={exitPrice}
              onChange={e => { setExitPrice(e.target.value); setError(''); }}
              autoFocus
            />
            {error && <div className="modal-error">{error}</div>}
            {pct !== null && (
              <div className={`modal-preview ${parseFloat(pct) >= 0 ? 'positive' : 'negative'}`}>
                Estimated P&L: {parseFloat(pct) >= 0 ? '+' : ''}{pct}%
              </div>
            )}
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-confirm">Confirm Exit</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Log entry card ───────────────────────────────────────────────────────────

function LogCard({ entry, onLogExit }) {
  const navigate   = useNavigate();
  const isClosed   = entry.status === 'closed';
  const isLong     = entry.direction === 'long';
  const gainSign   = entry.percentGain >= 0 ? 'positive' : 'negative';

  // Indicator snapshot (varies by strategy)
  const ind = entry.indicators || {};
  const weekly   = ind.weekly   || {};
  const daily    = ind.daily    || {};
  const fourHour = ind.fourHour || {};
  const rb       = ind.rubberband || {};

  return (
    <div className={`log-card ${entry.direction} ${isClosed ? 'closed' : 'open'}`}>
      {/* Top bar */}
      <div className="log-card-header">
        <div className="log-coin-identity" onClick={() => navigate(`/coin/${entry.coinId}`)}>
          {entry.image && (
            <img src={entry.image} alt={entry.name} className="log-coin-logo"
              onError={e => e.target.style.display = 'none'} />
          )}
          <div>
            <div className="log-coin-symbol">{entry.symbol}</div>
            <div className="log-coin-name">{entry.name}</div>
          </div>
        </div>
        <div className="log-badges">
          <StrategyBadge strategy={entry.strategy} />
          <span className={`dir-badge ${entry.direction}`}>
            {isLong ? '▲ LONG' : '▼ SHORT'}
          </span>
          <span className={`status-badge ${entry.status}`}>{entry.status}</span>
        </div>
      </div>

      {/* Date + prices */}
      <div className="log-price-row">
        <div className="log-price-cell">
          <div className="log-cell-label">Entry</div>
          <div className="log-cell-value">{formatPrice(entry.entryPrice)}</div>
          <div className="log-cell-sub">{formatDate(entry.date)} {formatTime(entry.date)}</div>
        </div>

        {isClosed ? (
          <>
            <div className="log-arrow">→</div>
            <div className="log-price-cell">
              <div className="log-cell-label">Exit</div>
              <div className="log-cell-value">{formatPrice(entry.exitPrice)}</div>
            </div>
            <div className="log-arrow">→</div>
            <div className="log-price-cell">
              <div className="log-cell-label">P&L</div>
              <div className={`log-cell-value pnl ${gainSign}`}>
                {entry.percentGain != null
                  ? `${entry.percentGain >= 0 ? '+' : ''}${entry.percentGain.toFixed(2)}%`
                  : 'N/A'}
              </div>
            </div>
          </>
        ) : (
          <button className="btn-log-exit" onClick={() => onLogExit(entry)}>
            Log Exit
          </button>
        )}
      </div>

      {/* Indicator snapshot */}
      <div className="log-indicators">
        {(entry.strategy === 'Rubberband' || entry.strategy === 'RBPullback') && rb.state && (
          <>
            <IndicatorPill label="TF" value={rb.timeframe} />
            <IndicatorPill label="State" value={rb.state?.toUpperCase()} color={rb.state} />
            {rb.pctFromSMA != null && (
              <IndicatorPill label="SMA dist" value={`${Math.abs(rb.pctFromSMA).toFixed(2)}%`} />
            )}
          </>
        )}
        {(entry.strategy === 'Triple Alignment' || entry.strategy === 'RSI-MTF') && (
          <>
            {weekly.rsi   != null && <IndicatorPill label="W RSI"  value={formatRSI(weekly.rsi)}   color={weekly.rsi   > 50 ? 'bull' : 'bear'} />}
            {daily.rsi    != null && <IndicatorPill label="D RSI"  value={formatRSI(daily.rsi)}    color={daily.rsi    > 50 ? 'bull' : 'bear'} />}
            {fourHour.rsi != null && <IndicatorPill label="4H RSI" value={formatRSI(fourHour.rsi)} color={fourHour.rsi > 50 ? 'bull' : 'bear'} />}
            {weekly.trend    && <IndicatorPill label="W trend"  value={weekly.trend}    color={weekly.trend.toLowerCase()}    />}
            {daily.trend     && <IndicatorPill label="D trend"  value={daily.trend}     color={daily.trend.toLowerCase()}     />}
            {fourHour.trend  && <IndicatorPill label="4H trend" value={fourHour.trend}  color={fourHour.trend.toLowerCase()}  />}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function AlertsLogPage() {
  const { tier } = useAuth();
  const [log, setLog]                 = useState([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState(null);
  const [exitTarget, setExitTarget]   = useState(null);
  const [filterStrategy, setFilterStrategy] = useState('all');

  const load = useCallback(async () => {
    if (!isServerApiAvailable()) {
      setError('Alerts log is only available in VPS mode.');
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const { log: entries } = await fetchAlertsLog();
      setLog(entries || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleExitConfirm(entryId, exitPrice) {
    try {
      const { entry } = await updateAlertLogEntry(entryId, { exitPrice });
      setLog(prev => prev.map(e => e.id === entryId ? entry : e));
      setExitTarget(null);
    } catch (err) {
      alert(`Failed to update entry: ${err.message}`);
    }
  }

  // ── Tier filter ──────────────────────────────────────────────────────────────
  // Only show entries for strategies the current tier has access to — matches
  // the UI-only gating pattern used everywhere else in this app (ProtectedRoute,
  // hasAccess), not a hard server-side boundary.
  const visibleLog = log.filter(e => {
    const requiredTier = getStrategyTier(e.strategy);
    return requiredTier ? hasAccess(tier, requiredTier) : true;
  });

  // ── Filtering ────────────────────────────────────────────────────────────────
  // Always show a chip for every strategy the user's tier can access, even if
  // it has zero alerts logged yet — unioned with whatever's actually in the
  // log so legacy/unmapped labels (e.g. old 'Rubberband' entries) still show.
  const accessibleStrategyLabels = ALL_STRATEGIES
    .filter(s => hasAccess(tier, s.tier))
    .map(s => CURRENT_LOG_LABEL[s.key] || s.label);
  const strategies = ['all', ...new Set([...accessibleStrategyLabels, ...visibleLog.map(e => e.strategy)])];

  const filtered = visibleLog.filter(e => {
    if (filterStrategy !== 'all' && e.strategy !== filterStrategy) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="log-page">
        <h1 className="log-page-title">📓 Alerts Log</h1>
        <div className="log-loading-center"><Loader size="large" text="Loading alerts log…" /></div>
      </div>
    );
  }

  return (
    <div className="log-page">
      {/* Header */}
      <div className="log-page-header">
        <div>
          <h1 className="log-page-title">📓 Alerts Log</h1>
          <p className="log-page-subtitle">Historical record of every fired alert — log your exits to track P&amp;L.</p>
        </div>
        <button className="log-refresh-btn" onClick={load}>↻ Refresh</button>
      </div>

      {error && (
        <ErrorBanner message={error} type="error" onDismiss={() => setError(null)} onRetry={load} />
      )}

      {/* Filters */}
      <div className="log-filters">
        <div className="log-filter-group">
          <span className="filter-label">Strategy:</span>
          {strategies.map(s => (
            <button key={s}
              className={`filter-btn ${filterStrategy === s ? 'active' : ''}`}
              onClick={() => setFilterStrategy(s)}
            >{s}</button>
          ))}
        </div>
      </div>

      {/* Entries */}
      {filtered.length === 0 ? (
        <div className="log-empty">
          <div className="log-empty-icon">📭</div>
          <div className="log-empty-text">
            {visibleLog.length === 0 ? 'No alerts have fired yet.' : 'No entries match your filters.'}
          </div>
          {visibleLog.length === 0 && (
            <div className="log-empty-sub">
              Alerts are generated automatically when RBPullback, triple alignment, or RSI-MTF conditions are met.
            </div>
          )}
        </div>
      ) : (
        <div className="log-grid">
          {filtered.map(entry => (
            <LogCard key={entry.id} entry={entry} onLogExit={setExitTarget} />
          ))}
        </div>
      )}

      {/* Exit modal */}
      {exitTarget && (
        <ExitModal
          entry={exitTarget}
          onConfirm={handleExitConfirm}
          onClose={() => setExitTarget(null)}
        />
      )}
    </div>
  );
}

export default AlertsLogPage;
